/**
 * Tailwind CSS Class Name Utility (cn)
 * Merges class names and prevents conflicts
 * Lightweight alternative to tailwind-merge + clsx
 */

type ClassValue = string | number | boolean | undefined | null | ClassValue[];

/**
 * Merge multiple class names into one string
 * Removes duplicates and handles conditional classes
 */
export function cn(...inputs: ClassValue[]): string {
  const classes: string[] = [];

  inputs.forEach((input) => {
    if (!input) return;

    if (typeof input === 'string') {
      classes.push(input);
    } else if (Array.isArray(input)) {
      const merged = cn(...input);
      if (merged) classes.push(merged);
    } else if (typeof input === 'object') {
      Object.entries(input).forEach(([key, value]) => {
        if (value) classes.push(key);
      });
    }
  });

  // Remove duplicates while preserving order
  return [...new Set(classes.join(' ').split(' '))].filter(Boolean).join(' ');
}

/**
 * Tailwind class conflict resolution
 * Resolves conflicts between similar Tailwind classes
 * Example: cn('px-4 px-6') => 'px-6' (last one wins)
 */
export function tw(...inputs: ClassValue[]): string {
  const merged = cn(...inputs);
  const classes = merged.split(' ');
  const resolved: Record<string, string> = {};

  // Tailwind class prefixes that conflict
  const conflictPrefixes = [
    'p-', 'px-', 'py-', 'pt-', 'pr-', 'pb-', 'pl-',
    'm-', 'mx-', 'my-', 'mt-', 'mr-', 'mb-', 'ml-',
    'w-', 'h-', 'min-w-', 'min-h-', 'max-w-', 'max-h-',
    'text-', 'font-', 'leading-', 'tracking-',
    'bg-', 'border-', 'rounded-',
    'opacity-', 'z-',
    'gap-', 'space-x-', 'space-y-',
    'flex-', 'grid-', 'col-', 'row-',
  ];

  classes.forEach((cls) => {
    const prefix = conflictPrefixes.find((p) => cls.startsWith(p));
    if (prefix) {
      // Last one wins for conflicting classes
      resolved[prefix] = cls;
    } else {
      // Non-conflicting classes
      resolved[cls] = cls;
    }
  });

  return Object.values(resolved).join(' ');
}

export default cn;





